import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { NotificationCard } from '@sighthost/ui';
import { announcementService } from '../services/announcementService';

/**
 * AnnouncementBanner (W5, 2026-08-11) — mounted once per app shell, before
 * the router Outlet (NOT in the sticky subNav slot, which section pages
 * already use). Dismissals are per-announcement in localStorage; a new
 * announcement id renders regardless of prior dismissals.
 */

const DISMISS_PREFIX = 'dismissed_announcement_';

function readDismissed(id: string): boolean {
  try { return localStorage.getItem(DISMISS_PREFIX + id) === '1'; }
  catch { return false; }
}

export function AnnouncementBanner({ audience }: { audience: 'customers' | 'staff' }) {
  const { data } = useQuery({
    queryKey: ['announcements', 'active', audience],
    queryFn: () => announcementService.listActive(audience),
  });
  const [dismissedTick, setDismissedTick] = useState(0);

  const visible = (data ?? []).filter((a) => !readDismissed(a.id));
  void dismissedTick;
  if (visible.length === 0) return null;

  return (
    <div className="mb-4 space-y-2">
      {visible.map((a) => (
        <div key={a.id} className="relative">
          <NotificationCard tone={a.tone} title={a.title} body={a.body} />
          <button
            type="button"
            aria-label="Dismiss announcement"
            onClick={() => {
              try { localStorage.setItem(DISMISS_PREFIX + a.id, '1'); } catch { /* private mode */ }
              setDismissedTick((t) => t + 1);
            }}
            className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}

export default AnnouncementBanner;
